import _ from 'lodash'
import dayjs from 'dayjs'
import React, { useEffect, useMemo } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { ConsultationType } from '@/core/models/consultation.model'

import ConsultationsSearchForm, { ConsultationsSearchFormProps, ConsultationsSearchFormValues } from '@/pages/consultations/components/ConsultationsSearchForm'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { getConsultations, selectConsultationSearchParams } from '@/redux/reducers/consultation.reducer'
import { getPatients, selectPatients } from '@/redux/reducers/patient.reducer'
import { getStaffs, selectStaffs } from '@/redux/reducers/staff.reducer'

import { limitOptions } from './ConsultationsTable'
import { useTheme, useMediaQuery } from '@mui/material'
import ConsultationsSearchFormMobile from '../components/ConsultationsSearchFormMobile'

export type ConsultationsSearchFormContainerProps = Pick<ConsultationsSearchFormProps, 'direction'>

export function ConsultationsSearchFormContainer(props: ConsultationsSearchFormContainerProps) {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const consultationSearchParams = useAppSelector(selectConsultationSearchParams)
  const patients = useAppSelector(selectPatients)
  const staffs = useAppSelector(selectStaffs)

  const consultationTypes = useMemo(() => [ConsultationType.Classic, ConsultationType.Emergency, ConsultationType.MeetingOnly, ConsultationType.Local], [])

  const doctorOptions = useMemo(
    () => _.map(_.filter(staffs, ['status', 'MEDECIN']), (doctor) => ({ label: `${doctor.userInfo.name} ${doctor.userInfo.surname}`, value: doctor._id })),
    [staffs]
  )

  const patientOptions = useMemo(
    () =>
      _.map(patients, (patient) => ({
        label: `${patient.userInfo.name} ${patient.userInfo.surname}`,
        value: patient._id,
      })),
    [patients]
  )

  const initialValues = useMemo(
    () => ({
      doctor: '',
      patient: '',
      type: [..._.take(consultationTypes, 2), ConsultationType.Netsoins],
      startDate: {
        $gte: dayjs().subtract(1, 'months').toDate(),
        $lte: dayjs().add(1, 'months').toDate(),
      },
    }),
    [consultationTypes]
  )

  useEffect(() => {
    dispatch(getConsultations(transformData(initialValues), { limit: limitOptions[0], order: 'DESC', orderBy: 'startDate' }))
  }, [dispatch, initialValues])

  const transformData = (values: ConsultationsSearchFormValues) => {
    return {
      medecin: values.doctor || undefined,
      patient: values.patient || undefined,
      type: [...values.type, ConsultationType.Netsoins],
      startDate: {
        $gte: values.startDate.$gte ? dayjs(values.startDate.$gte).startOf('day').toISOString() : undefined,
        $lte: values.startDate.$lte ? dayjs(values.startDate.$lte).endOf('day').toISOString() : undefined,
      },
    }
  }

  const handleSearchConsultations = (values: ConsultationsSearchFormValues) => {
    const data = transformData(values)
    dispatch(getConsultations(data, { limit: consultationSearchParams?.options?.limit }))
  }

  const handleSearchDoctor = useDebouncedCallback((value: string) => {
    const [name, surname] = value.split(' ')
    dispatch(getStaffs({ name, surname }))
  }, 300)

  const handleSearchPatient = useDebouncedCallback((value: string) => {
    dispatch(getPatients({ smartSearch: value || undefined }))
  }, 300)

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <>
      {!isMobile ? (
        <ConsultationsSearchForm
          {...props}
          consultationTypes={consultationTypes}
          doctorOptions={doctorOptions}
          initialValues={initialValues}
          onReset={handleSearchConsultations}
          onSearchDoctor={handleSearchDoctor}
          onSearchPatient={handleSearchPatient}
          onSubmit={handleSearchConsultations}
          patientOptions={patientOptions}
        />
      ) : (
        <ConsultationsSearchFormMobile
          {...props}
          consultationTypes={consultationTypes}
          doctorOptions={doctorOptions}
          initialValues={initialValues}
          onReset={handleSearchConsultations}
          onSearchDoctor={handleSearchDoctor}
          onSearchPatient={handleSearchPatient}
          onSubmit={handleSearchConsultations}
          patientOptions={patientOptions}
        />
      )}
    </>
  )
}

export default React.memo(ConsultationsSearchFormContainer)
